import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Avatar,
  Stack,
  ButtonGroup,
  Button,
  LinearProgress
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Eco as EcoIcon,
  Factory as FactoryIcon,
  LocalGasStation as GasIcon,
  ElectricBolt as ElectricIcon,
  Water as WaterIcon,
  Close as CloseIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  EmojiNature as NatureIcon,
  BusinessCenter as BusinessIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import MDButton from '../../components/MDButton';
import CarbonCertificate from './CarbonCertificate';
import { formatEmissions, getScopeColor, getCategoryIcon } from '../../utils/calculateCarbon';

/**
 * Karbon ayak izi hesaplama sonuçlarını gösteren rapor component'i
 */
const CarbonFootprintReport = ({ calculationData, onReset }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [factorsOpen, setFactorsOpen] = useState(false);
  const [certificateOpen, setCertificateOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Akıllı öneriler sistemi
  const generateSmartRecommendations = (results, activities, sectorMetrics, formData) => {
    const recommendations = [];
    const totalEmissions = results.total_emissions.ton_co2e;
    
    // Scope bazlı analizler
    const scopeAnalysis = {
      scope1: results.scope_totals.scope_1.ton_co2e,
      scope2: results.scope_totals.scope_2.ton_co2e,
      scope3: results.scope_totals.scope_3.ton_co2e
    };
    
    // En yüksek emisyon kaynağını bul
    const highestScope = Object.entries(scopeAnalysis).reduce((a, b) => 
      scopeAnalysis[a[0]] > scopeAnalysis[b[0]] ? a : b
    );
    
    // Aktivite bazlı analizler
    const topActivities = results.activities
      .filter(activity => !activity.error && activity.emissions_ton_co2e > 0)
      .sort((a, b) => b.emissions_ton_co2e - a.emissions_ton_co2e)
      .slice(0, 5);
    
    // Yüksek emisyonlu aktiviteler için öneriler
    topActivities.forEach((activity, index) => {
      const percentage = (activity.emissions_ton_co2e / totalEmissions * 100).toFixed(1);
      
      if (activity.activity.includes('electricity')) {
        recommendations.push({
          type: 'energy',
          priority: index < 2 ? 'high' : 'medium',
          title: `Elektrik Tüketimi Optimizasyonu`,
          description: `Elektrik tüketiminiz toplam emisyonların %${percentage}'ini oluşturuyor. LED aydınlatma, enerji verimli cihazlar ve akıllı kontrol sistemleri kullanın.`,
          potentialSaving: '15-25',
          icon: '⚡',
          actions: [
            'LED aydınlatmaya geçiş',
            'Akıllı termostat kullanımı', 
            'Enerji verimli cihaz tercihi',
            'Güneş enerjisi paneli kurulumu'
          ]
        });
      }
      
      if (activity.activity.includes('natural_gas') || activity.activity.includes('heating')) {
        recommendations.push({
          type: 'heating',
          priority: index < 2 ? 'high' : 'medium',
          title: `Isıtma Sistemi Verimliliği`,
          description: `Doğal gaz/ısıtma tüketiminiz %${percentage} emisyon yaratıyor. Yalıtım iyileştirmesi ve verimli ısıtma sistemleri önerilir.`,
          potentialSaving: '20-30',
          icon: '🔥',
          actions: [
            'Bina yalıtımı iyileştirmesi',
            'Kondensing kazan kullanımı',
            'Isı geri kazanım sistemi',
            'Akıllı ısıtma kontrolleri'
          ]
        });
      }
      
      if (activity.activity.includes('transport') || activity.activity.includes('fuel')) {
        recommendations.push({
          type: 'transport',
          priority: index < 2 ? 'high' : 'medium',
          title: `Ulaşım Emisyonları Azaltma`,
          description: `Ulaşım kaynaklı emisyonlarınız %${percentage} seviyesinde. Hibrit/elektrikli araçlar ve alternatif ulaşım yöntemleri değerlendirin.`,
          potentialSaving: '25-40',
          icon: '🚗',
          actions: [
            'Elektrikli/hibrit araç filosu',
            'Toplu taşıma teşviki',
            'Uzaktan çalışma politikaları',
            'Kargo/teslimat optimizasyonu'
          ]
        });
      }
    });
    
    // Sektör özel öneriler
    if (formData.sector === 'hotel') {
      if (sectorMetrics.room_count && sectorMetrics.occupancy_rate) {
        const emissionPerRoom = totalEmissions / sectorMetrics.room_count;
        const emissionPerGuest = totalEmissions / (sectorMetrics.room_count * sectorMetrics.occupancy_rate / 100);
        
        if (emissionPerRoom > 50) {
          recommendations.push({
            type: 'hotel',
            priority: 'high',
            title: `Otel Enerji Verimliliği`,
            description: `Oda başına emisyon ${emissionPerRoom.toFixed(1)} kg CO2e. Sektör ortalaması 45 kg'ın üzerinde. Acil önlemler gerekli.`,
            potentialSaving: '30-45',
            icon: '🏨',
            actions: [
              'Oda kartı sistemi ile enerji kontrolü',
              'Minibar enerji verimliliği',
              'Klima otomasyonu',
              'Otel aydınlatma optimizasyonu'
            ]
          });
        }
        
        if (sectorMetrics.restaurant_covers > 0) {
          recommendations.push({
            type: 'restaurant',
            priority: 'medium',
            title: `Restoran Operasyonu Verimliliği`,
            description: `Restoran operasyonlarında enerji verimliliği ve atık azaltma fırsatları mevcut.`,
            potentialSaving: '15-25',
            icon: '🍽️',
            actions: [
              'Mutfak ekipmanları verimliliği',
              'Soğuk hava deposu optimizasyonu',
              'Gıda israfı azaltma',
              'Kompost sistemi kurulumu'
            ]
          });
        }
      }
    }
    
    // Genel öneriler
    if (totalEmissions > 100) {
      recommendations.push({
        type: 'general',
        priority: 'medium',
        title: `Karbon Ayak İzi Takip Sistemi`,
        description: `Yüksek emisyon seviyeniz için sürekli izleme ve raporlama sistemi kurulması önerilir.`,
        potentialSaving: '10-20',
        icon: '📊',
        actions: [
          'Aylık karbon takibi',
          'Hedef belirleme',
          'Personel eğitimleri',
          'Sürdürülebilirlik sertifikasyonu'
        ]
      });
    }
    
    return recommendations.slice(0, 6); // En önemli 6 öneriyi döndür
  };

  // Performans puanı hesaplama
  const calculatePerformanceScore = (results, formData) => {
    const totalEmissions = results.total_emissions.ton_co2e;
    let score = 100;
    
    // Sektör karşılaştırması
    const sectorBenchmarks = {
      hotel: { excellent: 30, good: 50, average: 80 },
      manufacturing: { excellent: 100, good: 200, average: 400 },
      office: { excellent: 5, good: 15, average: 30 },
      retail: { excellent: 20, good: 40, average: 70 }
    };
    
    const benchmark = sectorBenchmarks[formData.sector] || sectorBenchmarks.office;
    
    if (totalEmissions <= benchmark.excellent) {
      score = 95 + Math.random() * 5;
    } else if (totalEmissions <= benchmark.good) {
      score = 80 + Math.random() * 15;
    } else if (totalEmissions <= benchmark.average) {
      score = 60 + Math.random() * 20;
    } else {
      score = 30 + Math.random() * 30;
    }
    
    return Math.round(score);
  };

  // Enhanced Card Component
  const MetricCard = ({ title, value, unit, icon, color, subtitle, percentage, trend }) => (
    <Zoom in={true} style={{ transitionDelay: '100ms' }}>
      <Card 
        sx={{ 
          height: '100%',
          background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
          border: `2px solid ${color}20`,
          borderRadius: 3,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 24px ${color}25`,
            border: `2px solid ${color}40`,
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ 
              bgcolor: color, 
              mr: 2, 
              width: 56, 
              height: 56,
              boxShadow: `0 4px 12px ${color}40`
            }}>
              {icon}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" color="text.primary" fontWeight="600">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {trend && (
              <TrendingUpIcon sx={{ color: trend > 0 ? 'success.main' : 'error.main' }} />
            )}
          </Box>
          
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: color,
                fontWeight: 'bold',
                mb: 0.5,
                fontSize: isMobile ? '2rem' : '2.5rem'
              }}
            >
              {value}
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight="500">
              {unit}
            </Typography>
          </Box>
          
          {percentage && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Toplam içindeki pay
                </Typography>
                <Typography variant="body2" fontWeight="600" color={color}>
                  %{percentage}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={percentage} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: `${color}15`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: color,
                    borderRadius: 4,
                  }
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );

  if (!calculationData || !calculationData.results) {
    return null;
  }

  const { results, validation, formData, activities, sectorMetrics } = calculationData;

  // Akıllı önerileri hesapla
  const smartRecommendations = generateSmartRecommendations(results, activities, sectorMetrics, formData);
  const performanceScore = calculatePerformanceScore(results, formData);
  
  // Performans kategorisini belirle
  const getPerformanceCategory = (score) => {
    if (score >= 90) return { label: 'Mükemmel', color: '#4caf50' };
    if (score >= 75) return { label: 'İyi', color: '#2196f3' };
    if (score >= 60) return { label: 'Orta', color: '#ff9800' };
    return { label: 'Geliştirilmeli', color: '#f44336' };
  };
  
  const performanceCategory = getPerformanceCategory(performanceScore);

  // Grafik için veri hazırlama
  const scopeChartData = [
    {
      name: 'Scope 1 (Doğrudan)',
      value: results.scope_totals.scope_1.ton_co2e,
      color: getScopeColor('1')
    },
    {
      name: 'Scope 2 (Enerji)',
      value: results.scope_totals.scope_2.ton_co2e,
      color: getScopeColor('2')
    },
    {
      name: 'Scope 3 (Dolaylı)',
      value: results.scope_totals.scope_3.ton_co2e,
      color: getScopeColor('3')
    }
  ].filter(item => item.value > 0);

  const activitiesChartData = results.activities
    .filter(activity => !activity.error && activity.emissions_ton_co2e > 0)
    .sort((a, b) => b.emissions_ton_co2e - a.emissions_ton_co2e)
    .slice(0, 10) // Top 10 aktivite
    .map(activity => ({
      name: activity.activity.replace(/_/g, ' '),
      value: activity.emissions_ton_co2e,
      scope: `Scope ${activity.scope}`
    }));

  // CSV Export
  const handleCSVExport = () => {
    try {
      const csvContent = results.csvData || generateCSVContent();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `carbon_footprint_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('CSV export error:', error);
    }
  };

  // PDF Export
  const handlePDFExport = () => {
    try {
      const doc = new jsPDF();
      
      // Başlık
      doc.setFontSize(20);
      doc.text('Karbon Ayak İzi Raporu', 20, 20);
      
      // Tarih
      doc.setFontSize(12);
      doc.text(`Rapor Tarihi: ${new Date(results.calculation_date).toLocaleDateString('tr-TR')}`, 20, 35);
      doc.text(`Sektör: ${formData.sector}`, 20, 45);
      
      // Özet tablo
      const summaryData = [
        ['Scope 1 (Doğrudan Emisyonlar)', `${results.scope_totals.scope_1.ton_co2e.toFixed(2)} tCO2e`],
        ['Scope 2 (Enerji Emisyonları)', `${results.scope_totals.scope_2.ton_co2e.toFixed(2)} tCO2e`],
        ['Scope 3 (Dolaylı Emisyonlar)', `${results.scope_totals.scope_3.ton_co2e.toFixed(2)} tCO2e`],
        ['TOPLAM EMİSYON', `${results.total_emissions.ton_co2e.toFixed(2)} tCO2e`]
      ];
      
      doc.autoTable({
        head: [['Kategori', 'Emisyon Miktarı']],
        body: summaryData,
        startY: 60,
        theme: 'striped'
      });
      
      // Sektör yoğunluğu
      if (results.sector_intensity && results.sector_intensity.label) {
        const intensityValue = Object.values(results.sector_intensity).find(val => typeof val === 'number');
        doc.autoTable({
          head: [['Sektör Yoğunluğu', 'Değer']],
          body: [[results.sector_intensity.label, `${intensityValue?.toFixed(2)} ${Object.keys(results.sector_intensity).find(key => typeof results.sector_intensity[key] === 'number')}`]],
          startY: doc.lastAutoTable.finalY + 10,
          theme: 'grid'
        });
      }
      
      // Aktiviteler detayı
      const activitiesData = results.activities
        .filter(activity => !activity.error)
        .map(activity => [
          activity.activity_name_tr || activity.activity.replace(/_/g, ' '),
          `${activity.amount.toLocaleString('tr-TR')} ${activity.unit}`,
          `${activity.emissions_ton_co2e.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} tCO2e`,
          activity.scope_name_tr || `Scope ${activity.scope}`
        ]);
      
      doc.autoTable({
        head: [['Aktivite', 'Miktar', 'Emisyon', 'Scope']],
        body: activitiesData,
        startY: doc.lastAutoTable.finalY + 10,
        theme: 'striped'
      });
      
      doc.save(`carbon_footprint_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
    }
  };

  const generateCSVContent = () => {
    const csvRows = [];
    
    // Header
    csvRows.push('Karbon Ayak İzi Raporu');
    csvRows.push(`Tarih,${new Date(results.calculation_date).toLocaleDateString('tr-TR')}`);
    csvRows.push(`Sektör,${formData.sector}`);
    csvRows.push('');
    
    // Özet
    csvRows.push('ÖZET');
    csvRows.push('Kategori,Emisyon (tCO2e)');
    csvRows.push(`Scope 1,${results.scope_totals.scope_1.ton_co2e.toFixed(2)}`);
    csvRows.push(`Scope 2,${results.scope_totals.scope_2.ton_co2e.toFixed(2)}`);
    csvRows.push(`Scope 3,${results.scope_totals.scope_3.ton_co2e.toFixed(2)}`);
    csvRows.push(`TOPLAM,${results.total_emissions.ton_co2e.toFixed(2)}`);
    csvRows.push('');
    
    // Aktiviteler
    csvRows.push('AKTİVİTELER');
    csvRows.push('Aktivite,Miktar,Birim,Emisyon (kgCO2e),Emisyon (tCO2e),Scope,Kategori');
    results.activities.forEach(activity => {
      if (!activity.error) {
        csvRows.push([
          activity.activity_name_tr || activity.activity,
          activity.amount.toLocaleString('tr-TR'),
          activity.unit,
          activity.emissions_kg_co2e.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          activity.emissions_ton_co2e.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
          activity.scope_name_tr || `Scope ${activity.scope}`,
          activity.category_name_tr || activity.category
        ].join(','));
      }
    });
    
    return csvRows.join('\n');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Modern Header */}
      <Fade in={true}>
        <Paper 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 4,
            borderRadius: 4,
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.1
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64,
                  backdropFilter: 'blur(10px)'
                }}>
              
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "h5" : "h3"} 
                    fontWeight="bold" 
                    sx={{ mb: 1 }}
                  >
                    Karbon Ayak İzi Raporu
                  </Typography>
                  {formData.organizationName && (
                    <Typography variant="h6" sx={{ opacity: 0.95, mb: 1 }}>
                      📍 {formData.organizationName}
                    </Typography>
                  )}
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    🌱 Sürdürülebilirlik Analizi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Hesaplama Tarihi: {new Date(results.calculation_date).toLocaleDateString('tr-TR')}
                    {formData.periodType === 'specific' && formData.startDate && formData.endDate && (
                      <span> | Rapor Dönemi: {new Date(formData.startDate).toLocaleDateString('tr-TR')} - {new Date(formData.endDate).toLocaleDateString('tr-TR')}</span>
                    )}
                    {formData.periodType === 'monthly' && formData.month && formData.year && (
                      <span> | Rapor Dönemi: {formData.month}/{formData.year}</span>
                    )}
                    {formData.periodType === 'yearly' && formData.year && (
                      <span> | Rapor Dönemi: {formData.year} Yılı</span>
                    )}
                  </Typography>
                </Box>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title="CSV olarak indir">
                  <IconButton 
                    onClick={handleCSVExport} 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                  >
                    <CsvIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="PDF olarak indir">
                  <IconButton 
                    onClick={handlePDFExport} 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                  >
                    <PdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sertifika Oluştur">
                  <IconButton 
                    onClick={() => setCertificateOpen(true)} 
                    sx={{ 
                      bgcolor: 'rgba(255,215,0,0.3)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,215,0,0.5)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,215,0,0.5)',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>🏆</span>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Detayları göster">
                  <IconButton 
                    onClick={() => setDetailsOpen(true)}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {/* Validasyon Mesajları */}
      {validation && (
        <Box sx={{ mb: 3 }}>
          {validation.errors?.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Hatalar:</Typography>
              {validation.errors.map((error, index) => (
                <Typography key={index} variant="body2">• {error}</Typography>
              ))}
            </Alert>
          )}
          {validation.warnings?.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2">Uyarılar:</Typography>
              {validation.warnings.map((warning, index) => (
                <Typography key={index} variant="body2">• {warning}</Typography>
              ))}
            </Alert>
          )}
        </Box>
      )}

      {/* Performans Dashboard */}
      <Fade in={true} style={{ transitionDelay: '200ms' }}>
        <Paper 
          elevation={12}
          sx={{ 
            p: 4, 
            borderRadius: 4, 
            mb: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            gutterBottom 
            sx={{ 
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 4
            }}
          >
            📊 Performans Gösterge Paneli
          </Typography>
          
          <Grid container spacing={4}>
            {/* Genel Performans */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" fontWeight="bold" gutterBottom>
                  {performanceScore}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  Genel Performans Puanı
                </Typography>
                <Chip 
                  label={performanceCategory.label}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                />
              </Box>
            </Grid>
            
            {/* Emisyon Yoğunluğu */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {(results.total_emissions.ton_co2e).toFixed(1)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                  Toplam Emisyon (tCO2e)
                </Typography>
                {results.sector_intensity && (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Yoğunluk: {Object.values(results.sector_intensity).find(val => typeof val === 'number')?.toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* En Yüksek Scope */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                {(() => {
                  const maxScope = Object.entries({
                    'Scope 1': results.scope_totals.scope_1.ton_co2e,
                    'Scope 2': results.scope_totals.scope_2.ton_co2e,
                    'Scope 3': results.scope_totals.scope_3.ton_co2e
                  }).reduce((a, b) => a[1] > b[1] ? a : b);
                  
                  return (
                    <>
                      <Typography variant="h3" fontWeight="bold" gutterBottom>
                        {maxScope[1].toFixed(1)}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                        En Yüksek: {maxScope[0]}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {((maxScope[1] / results.total_emissions.ton_co2e) * 100).toFixed(1)}% oranında
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Grid>
          </Grid>
          
          {/* Benchmark Karşılaştırması */}
          <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🎯 Sektör Karşılaştırması
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Mükemmel</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.light">
                    ≤ 30 tCO2e
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>İyi</Typography>
                  <Typography variant="h6" fontWeight="bold" color="info.light">
                    30-50 tCO2e
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Ortalama</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.light">
                    50-80 tCO2e
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Geliştirilmeli</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.light">
                    &gt; 80 tCO2e
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Fade>

      {/* Ana Metrikler - Enhanced Design */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Toplam Emisyon"
            subtitle="Genel Karbon Ayak İzi"
            value={results.total_emissions.ton_co2e.toLocaleString('tr-TR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
            unit="tCO2e"
            icon={<AssessmentIcon />}
            color={theme.palette.error.main}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Scope 1"
            subtitle="Doğrudan Emisyonlar"
            value={results.scope_totals.scope_1.ton_co2e.toLocaleString('tr-TR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
            unit="tCO2e"
            icon={<FactoryIcon />}
            color={getScopeColor('1')}
            percentage={((results.scope_totals.scope_1.ton_co2e / results.total_emissions.ton_co2e) * 100).toFixed(1)}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Scope 2"
            subtitle="Dolaylı Enerji Emisyonları"
            value={results.scope_totals.scope_2.ton_co2e.toLocaleString('tr-TR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
            unit="tCO2e"
            icon={<ElectricIcon />}
            color={getScopeColor('2')}
            percentage={((results.scope_totals.scope_2.ton_co2e / results.total_emissions.ton_co2e) * 100).toFixed(1)}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard
            title="Scope 3"
            subtitle="Diğer Dolaylı Emisyonlar"
            value={results.scope_totals.scope_3.ton_co2e.toLocaleString('tr-TR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
            unit="tCO2e"
            icon={<BusinessIcon />}
            color={getScopeColor('3')}
            percentage={((results.scope_totals.scope_3.ton_co2e / results.total_emissions.ton_co2e) * 100).toFixed(1)}
          />
        </Grid>
      </Grid>

      {/* Sektör Yoğunluğu */}
      {results.sector_intensity && results.sector_intensity.label && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#e8f5e8' }}>
          <Typography variant="h6" gutterBottom color="primary">
            🎯 Sektör Yoğunluğu
          </Typography>
          <Typography variant="h4" color="success.main">
            {Object.values(results.sector_intensity).find(val => typeof val === 'number')?.toFixed(2)}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {results.sector_intensity.label}
          </Typography>
        </Paper>
      )}

      {/* Modern Grafikler Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Scope Dağılımı */}
        <Grid item xs={12} lg={6}>
          <Fade in={true} style={{ transitionDelay: '200ms' }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <AssessmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    Scope Dağılımı
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emisyon kategorileri analizi
                  </Typography>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={scopeChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value.toFixed(1)}t`}
                    labelLine={false}
                  >
                    {scopeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value) => [`${value.toFixed(2)} tCO2e`, 'Emisyon']}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Fade>
        </Grid>

        {/* Aktivite Dağılımı */}
        <Grid item xs={12} lg={6}>
          <Fade in={true} style={{ transitionDelay: '300ms' }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    En Yüksek Emisyonlar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktivite bazlı analiz
                  </Typography>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={activitiesChartData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.3)" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={80}
                    fontSize={12}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [`${value.toFixed(3)} tCO2e`, 'Emisyon']}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="url(#colorGradient)"
                    radius={[0, 4, 4, 0]}
                  />
                  <defs>
                    <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#ff7300" />
                      <stop offset="100%" stopColor="#ff3300" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      {/* Hotel Analytics Bölümü */}
      {results.hotelAnalytics && (
        <Fade in={true} style={{ transitionDelay: '350ms' }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              borderRadius: 4, 
              mb: 4,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 3 
            }}>
              <Avatar sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                width: 48, 
                height: 48 
              }}>
                <Assessment sx={{ color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  🏨 Otel Performans Analizi
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Otel sektörüne özel analiz ve öneriler
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Performans Puanı */}
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight="bold" gutterBottom>
                    {results.hotelAnalytics.performanceScore}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Performans Puanı
                  </Typography>
                  <Chip 
                    label={results.hotelAnalytics.performanceRating}
                    color={
                      results.hotelAnalytics.performanceRating === 'Mükemmel' ? 'success' :
                      results.hotelAnalytics.performanceRating === 'İyi' ? 'info' :
                      results.hotelAnalytics.performanceRating === 'Orta' ? 'warning' : 'error'
                    }
                    sx={{ mt: 1, fontWeight: 'bold' }}
                  />
                </Box>
              </Grid>

              {/* Oda Başına Emisyon */}
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {results.hotelAnalytics.emissionPerRoom.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    kg CO2e/oda/gece
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Sektör ortalaması: 45 kg
                  </Typography>
                </Box>
              </Grid>

              {/* Misafir Başına Emisyon */}
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {results.hotelAnalytics.emissionPerGuest.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    kg CO2e/misafir/gece
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Sektör ortalaması: 25 kg
                  </Typography>
                </Box>
              </Grid>

              {/* Enerji Yoğunluğu */}
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {results.hotelAnalytics.energyIntensity.toFixed(1)}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    kWh/m²/yıl
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                    Sektör ortalaması: 250 kWh
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Öneriler */}
            {results.hotelAnalytics.recommendations && results.hotelAnalytics.recommendations.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  💡 Optimizasyon Önerileri
                </Typography>
                <Grid container spacing={2}>
                  {results.hotelAnalytics.recommendations.slice(0, 3).map((recommendation, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.1)', 
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)'
                      }}>
                        <Typography variant="body1" fontWeight="500" gutterBottom>
                          {recommendation.title}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {recommendation.description}
                        </Typography>
                        {recommendation.potentialSaving && (
                          <Chip 
                            label={`${recommendation.potentialSaving}% tasarruf`}
                            size="small"
                            sx={{ 
                              mt: 1, 
                              bgcolor: 'rgba(76, 175, 80, 0.2)',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Fade>
      )}

      {/* Akıllı Öneriler Sistemi */}
      {smartRecommendations.length > 0 && (
        <Fade in={true} style={{ transitionDelay: '450ms' }}>
          <Paper 
            elevation={8}
            sx={{ 
              p: 4, 
              borderRadius: 4, 
              mb: 4,
              background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
              boxShadow: '0 20px 40px rgba(255, 154, 158, 0.3)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#d32f2f' }}>
                🎯 Akıllı Öneriler Sistemi
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', mb: 3 }}>
                Karbon ayak izinizi azaltmak için özel olarak hazırlanmış öneriler
              </Typography>
              
              {/* Performans Skoru */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 3,
                mb: 4 
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h2" fontWeight="bold" sx={{ color: performanceCategory.color }}>
                    {performanceScore}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Performans Puanı
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip 
                    label={performanceCategory.label}
                    sx={{ 
                      bgcolor: performanceCategory.color,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      px: 2,
                      py: 1
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sektör Karşılaştırması
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {smartRecommendations.map((recommendation, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      border: `2px solid ${
                        recommendation.priority === 'high' ? '#f44336' :
                        recommendation.priority === 'medium' ? '#ff9800' : '#4caf50'
                      }`,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" sx={{ mr: 2 }}>
                          {recommendation.icon}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {recommendation.title}
                          </Typography>
                          <Chip 
                            label={`${recommendation.priority.toUpperCase()} ÖNCELİK`}
                            size="small"
                            color={
                              recommendation.priority === 'high' ? 'error' :
                              recommendation.priority === 'medium' ? 'warning' : 'success'
                            }
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {recommendation.description}
                      </Typography>
                      
                      {recommendation.potentialSaving && (
                        <Box sx={{ 
                          bgcolor: '#e8f5e8', 
                          p: 2, 
                          borderRadius: 2, 
                          mb: 3,
                          textAlign: 'center'
                        }}>
                          <Typography variant="h6" color="success.main" fontWeight="bold">
                            %{recommendation.potentialSaving} Tasarruf Potansiyeli
                          </Typography>
                        </Box>
                      )}
                      
                      {recommendation.actions && (
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            💡 Eylem Planı:
                          </Typography>
                          <List dense>
                            {recommendation.actions.slice(0, 3).map((action, actionIndex) => (
                              <ListItem key={actionIndex} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <Typography variant="body2">•</Typography>
                                </ListItemIcon>
                                <ListItemText 
                                  primary={action}
                                  primaryTypographyProps={{ 
                                    variant: 'body2',
                                    color: 'text.secondary'
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Genel Öneriler Özeti */}
            <Box sx={{ 
              mt: 4, 
              p: 3, 
              bgcolor: 'rgba(255, 255, 255, 0.8)', 
              borderRadius: 3,
              border: '2px solid #e3f2fd'
            }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#1976d2' }}>
                📈 Önerilen İyileştirme Planı
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    🔴 Acil (0-3 ay):
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yüksek öncelikli önerileri uygulayın
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    🟡 Orta Vadeli (3-12 ay):
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Orta öncelikli projeleri planlayın
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    🟢 Uzun Vadeli (1-3 yıl):
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sürdürülebilirlik kültürü oluşturun
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            {/* Sertifika Oluştur Butonu */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setCertificateOpen(true)}
                startIcon={<span style={{ fontSize: '24px' }}>🏆</span>}
                sx={{
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: 'black',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  boxShadow: '0 8px 20px rgba(255, 215, 0, 0.4)',
                  border: '2px solid rgba(255, 215, 0, 0.6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 30px rgba(255, 215, 0, 0.6)',
                  }
                }}
              >
                🎖️ Sürdürülebilirlik Sertifikası Oluştur
              </Button>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mt: 2 }}>
                Performansınızı belgeleyin ve başarınızı paylaşın!
              </Typography>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Modern Aktiviteler Tablosu */}
      <Fade in={true} style={{ transitionDelay: '400ms' }}>
        <Paper 
          elevation={0}
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            background: 'rgba(255,255,255,0.1)', 
            backdropFilter: 'blur(10px)',
            p: 3,
            borderBottom: '1px solid rgba(255,255,255,0.2)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <TimelineIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" color="white" fontWeight="600">
                  Aktivite Detayları
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Emisyon kaynaklarının detaylı analizi
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <TableContainer sx={{ background: 'rgba(255,255,255,0.95)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { 
                  fontWeight: 'bold', 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  borderBottom: '2px solid rgba(102, 126, 234, 0.2)'
                }}}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NatureIcon color="primary" />
                      Aktivite
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <SpeedIcon color="primary" />
                      Miktar
                    </Box>
                  </TableCell>
                  <TableCell align="right">Emisyon (kgCO2e)</TableCell>
                  <TableCell align="right">Emisyon (tCO2e)</TableCell>
                  <TableCell align="center">Scope</TableCell>
                  <TableCell>Kategori</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.activities
                  .filter(activity => !activity.error && activity.emissions_kg_co2e > 0)
                  .sort((a, b) => b.emissions_kg_co2e - a.emissions_kg_co2e)
                  .map((activity, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { 
                          bgcolor: 'rgba(102, 126, 234, 0.05)' 
                        },
                        '&:hover': { 
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s ease-in-out'
                        },
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: getScopeColor(activity.scope) + '20',
                            color: getScopeColor(activity.scope),
                            width: 40,
                            height: 40
                          }}>
                            {activity.activity_name_tr ? 
                              activity.activity_name_tr.charAt(0) : 
                              activity.activity.charAt(0).toUpperCase()
                            }
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="600">
                              {activity.activity_name_tr || activity.activity.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {activity.source || 'Varsayılan faktör'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {activity.amount.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activity.unit}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="error.main">
                          {activity.emissions_kg_co2e.toLocaleString('tr-TR', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="error.dark">
                          {activity.emissions_ton_co2e.toLocaleString('tr-TR', { 
                            minimumFractionDigits: 3, 
                            maximumFractionDigits: 3 
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={activity.scope_name_tr || `Scope ${activity.scope}`}
                          size="small"
                          sx={{ 
                            bgcolor: getScopeColor(activity.scope) + '40',
                            color: getScopeColor(activity.scope),
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            '&:hover': {
                              bgcolor: getScopeColor(activity.scope) + '60'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                          {activity.category_name_tr || activity.category}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>

      {/* Modern Action Buttons */}
      <Fade in={true} style={{ transitionDelay: '500ms' }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            textAlign: 'center',
            mt: 4
          }}
        >
          <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
            📊 Rapor İşlemleri
          </Typography>
          
          <Stack 
            direction={isMobile ? "column" : "row"} 
            spacing={2} 
            justifyContent="center"
            alignItems="center"
          >
            <ButtonGroup 
              variant="contained" 
              size="large"
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{ 
                '& .MuiButton-root': { 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                  }
                }
              }}
            >
              <Button 
                onClick={() => setFactorsOpen(true)}
                startIcon={<InfoIcon />}
                sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' }}
              >
                Emisyon Faktörleri
              </Button>
              <Button 
                onClick={handleCSVExport}
                startIcon={<CsvIcon />}
                sx={{ background: 'linear-gradient(45deg, #11998e 30%, #38ef7d 90%)' }}
              >
                CSV İndir
              </Button>
              <Button 
                onClick={handlePDFExport}
                startIcon={<PdfIcon />}
                sx={{ background: 'linear-gradient(45deg, #fd746c 30%, #ff9068 90%)' }}
              >
                PDF İndir
              </Button>
            </ButtonGroup>
            
            {onReset && (
              <Button 
                variant="outlined" 
                size="large"
                onClick={onReset}
                startIcon={<NatureIcon />}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                }}
              >
                Yeni Hesaplama
              </Button>
            )}
          </Stack>
        </Paper>
      </Fade>

      {/* Detaylar Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>📋 Hesaplama Detayları</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemIcon><InfoIcon /></ListItemIcon>
              <ListItemText
                primary="Hesaplama Tarihi"
                secondary={new Date(results.calculation_date).toLocaleString('tr-TR')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><InfoIcon /></ListItemIcon>
              <ListItemText
                primary="Bölge"
                secondary={results.region}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><InfoIcon /></ListItemIcon>
              <ListItemText
                primary="Sektör"
                secondary={results.sector}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon /></ListItemIcon>
              <ListItemText
                primary="İşlenen Aktivite Sayısı"
                secondary={`${results.activities.filter(a => !a.error).length} / ${results.activities.length}`}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDetailsOpen(false)}>Kapat</MDButton>
        </DialogActions>
      </Dialog>

      {/* Emisyon Faktörleri Dialog */}
      <Dialog open={factorsOpen} onClose={() => setFactorsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>📚 Kullanılan Emisyon Faktörleri</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Aktivite</TableCell>
                  <TableCell align="right">Faktör</TableCell>
                  <TableCell>Kaynak</TableCell>
                  <TableCell>Bölge</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.emission_factors_used.map((factor, index) => (
                  <TableRow key={index}>
                    <TableCell>{factor.activity.replace(/_/g, ' ')}</TableCell>
                    <TableCell align="right">{factor.factor}</TableCell>
                    <TableCell>{factor.source}</TableCell>
                    <TableCell>{factor.region}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setFactorsOpen(false)}>Kapat</MDButton>
        </DialogActions>
      </Dialog>

      {/* Sertifika Modal */}
      {certificateOpen && (
        <CarbonCertificate 
          calculationData={calculationData}
          onClose={() => setCertificateOpen(false)}
        />
      )}
    </Container>
  );
};

export default CarbonFootprintReport;
